import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Table, Select, Input, Tag, Breadcrumb, Spin } from 'antd';
import { PlusOutlined, SearchOutlined, InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usersApi, User } from '../services/users';
import { message } from '../utils/message';
import { showConfirmModal } from '../components/PopupModal';

const { Title, Text } = Typography;
const { Option } = Select;

interface UserData {
  key: string;
  adminName: string;
  username: string;
  email: string;
  phone: string;
  role: string;
  createdAt: string;
  lastLogin: string;
}

const PermissionManagement: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<string>('전체');
  const [searchText, setSearchText] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const columns = [
    {
      title: '관리자명',
      dataIndex: 'adminName',
      key: 'adminName',
      width: 120,
    },
    {
      title: 'ID',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '이메일',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '휴대전화',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: '권한',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => {
        const color = role === 'super_admin' ? 'red' : 'blue';
        const text = role === 'super_admin' ? 'Super Admin' : 'Admin';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '최초등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
    },
    {
      title: '마지막 로그인 일시',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: 180,
    },
  ];

  // 사용자 데이터 로드
  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText || undefined,
        role: selectedRole !== '전체' ? selectedRole : undefined
      };

      const response = await usersApi.getUsers(params);

      if (response.success) {
        setUsers(response.data.users);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.totalItems
        }));
      }
    } catch (error: any) {
      console.error('사용자 목록 로드 실패:', error);
      message.error('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadUsers();
  }, [pagination.current, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // 검색어나 필터 변경 시 데이터 로드 (페이지를 1로 리셋)
  useEffect(() => {
    if (pagination.current === 1) {
      loadUsers();
    } else {
      setPagination(prev => ({ ...prev, current: 1 }));
    }
  }, [searchText, selectedRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  // 행 클릭 핸들러
  const handleRowClick = (record: UserData) => {
    navigate(`/permission-management/detail/${record.key}`);
  };

  // 페이지네이션 변경 핸들러
  const handleTableChange = (paginationInfo: any) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }));
  };



  // 체크박스 선택 핸들러
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // 일괄 삭제 핸들러
  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('삭제할 사용자를 선택해주세요.');
      return;
    }

    showConfirmModal({
      title: '사용자 삭제',
      content: `선택한 ${selectedRowKeys.length}명의 사용자를 삭제하시겠습니까?`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onConfirm: async () => {
        try {
          const ids = selectedRowKeys.map(key => parseInt(key.toString()));
          const response = await usersApi.bulkDeleteUsers(ids);

          if (response.success) {
            message.success(response.message);
            setSelectedRowKeys([]);
            loadUsers();
          }
        } catch (error: any) {
          console.error('일괄 삭제 실패:', error);
          message.error(error.response?.data?.message || '사용자 삭제에 실패했습니다.');
        }
      }
    });
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\./g, '.').slice(0, -1);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/\./g, '.').slice(0, -1);
  };

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '-';
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');
    // 010-XXXX-XXXX 형식으로 변환
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  };

  // 테이블 데이터 변환
  const tableData: UserData[] = users.map(user => ({
    key: user.id.toString(),
    adminName: user.name,
    username: user.username,
    email: user.email,
    phone: formatPhoneNumber(user.phone),
    role: user.role,
    createdAt: formatDate(user.createdAt),
    lastLogin: formatDateTime(user.lastLogin)
  }));

  // rowSelection 설정
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '권한 관리' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목과 안내 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 32px 0' }}>
        <Title level={2} style={{ margin: '0', color: '#ffffff' }}>
          권한 관리
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
          <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>
            권한 변경 필요 시 HNINE 문의 바랍니다. 02-521-0518
          </Text>
        </div>
      </div>

      {/* 컨트롤 영역 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text>권한</Text>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              style={{ width: 120 }}
            >
              <Option value="전체">전체</Option>
              <Option value="super_admin">Super Admin</Option>
              <Option value="admin">Admin</Option>
            </Select>
          </div>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/permission-management/add')}
        >
          추가
        </Button>
      </div>

      {/* 테이블 상단 정보 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Text type="secondary">총 {pagination.total}건</Text>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleBulkDelete}
            disabled={selectedRowKeys.length === 0}
          >
            삭제 ({selectedRowKeys.length})
          </Button>
        </div>
        <Input
          placeholder="검색"
          suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
          value={searchText}
            allowClear
          onChange={(e) => handleSearch(e.target.value)}
          style={{
            width: 400,
            height: 40,
            borderRadius: 8,
            background: '#2a2a2a',
            border: '1px solid #404040',
            color: '#ffffff'
          }}
        />
      </div>

      {/* 테이블 */}
      <Spin spinning={loading}>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={tableData}
          onRow={(record) => ({
            onClick: (e) => {
              // 체크박스 클릭 시 행 클릭 이벤트 무시
              const target = e.target as HTMLElement;
              if (target.closest('.ant-checkbox-wrapper')) {
                return;
              }
              handleRowClick(record);
            },
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: false,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
          scroll={{ x: 1000 }}
          size="middle"
          style={{
            background: '#1f1f1f',
          }}
        />
      </Spin>
    </Space>
  );
};

export default PermissionManagement;